/**
 * Module valuation-tenants defines tenant contracts and aggregates, including rent, rental
 * agreements, vacancy, rent-free periods, and shared management-cost parameters.
 *
 * Income-specific parameters and calculated tenant values are defined in
 * valuation-income.ts.
 */
import { z } from 'zod';
import { ISODateSchema, UUIDSchema } from './common';
import { AreaValueSchema, PercentageSchema } from './measurements';
import { MonetaryValueSchema, SquareMetreMoneyPerAreaValueSchema } from './money';
import { RentSummarySchema, TenantSummaryUsageTypeSchema } from './tenant-summary';
export const RentPaymentPeriodSchema = z.enum(['yearly', 'monthly']);
export const RentPaymentScopeSchema = z.enum(['per_area', 'per_unit', 'absolute']);
export const RentSchema = z.object({
    value: MonetaryValueSchema,
    payment_period: RentPaymentPeriodSchema,
    payment_scope: RentPaymentScopeSchema,
});
const PerUnitManagementCostParameterSchema = z.object({
    value: MonetaryValueSchema,
    cost_scope: z.literal('per_unit'),
});
const AbsoluteManagementCostParameterSchema = z.object({
    value: MonetaryValueSchema,
    cost_scope: z.literal('absolute'),
});
export const PerConstructionCostManagementCostParameterSchema = z.object({
    value: z.number(),
    cost_scope: z.literal('per_construction_cost'),
});
const PerYearlyRentManagementCostParameterSchema = z.object({
    value: z.number(),
    cost_scope: z.literal('per_yearly_rent'),
});
const PerAreaManagementCostParameterSchema = z.object({
    value: SquareMetreMoneyPerAreaValueSchema,
    cost_scope: z.literal('per_area'),
});
const PerMonthlyRentManagementCostParameterSchema = z.object({
    value: z.number(),
    cost_scope: z.literal('per_monthly_rent'),
});
export const ManagementCostParameterSchema = z.discriminatedUnion('cost_scope', [
    PerUnitManagementCostParameterSchema,
    AbsoluteManagementCostParameterSchema,
    PerConstructionCostManagementCostParameterSchema,
    PerYearlyRentManagementCostParameterSchema,
    PerAreaManagementCostParameterSchema,
    PerMonthlyRentManagementCostParameterSchema,
]);
const RentFreeSpecialPropertyFeatureSchema = z.object({
    apply: z.boolean(),
    adjusted_discount_rate: PercentageSchema.optional(),
    remaining_operating_cost: ManagementCostParameterSchema,
});
const RentFreePeriodSchema = z.object({
    start_date: ISODateSchema.optional(),
    end_date: ISODateSchema.optional(),
    end_date_mode: z.enum(['date', 'duration']).optional(),
    duration: z.number().positive().multipleOf(0.5).optional(),
    special_property_feature_parameter: RentFreeSpecialPropertyFeatureSchema.optional(),
});
const ContractualOptionsSchema = z.object({
    frequency: z.number(),
    years: z.number(),
});
const BaseRentalAgreementSchemaShape = {
    rent: RentSchema,
    lease_number: z.string().optional(),
    start_date: ISODateSchema.optional(),
    tenant_name: z.string().optional(),
    end_date: ISODateSchema.optional(),
    options: ContractualOptionsSchema.optional(),
    private_person: z.boolean(),
    remaining_years: z.number().optional(),
    rent_free_periods: RentFreePeriodSchema.array(),
};
const FixedRentalAgreementSchema = z.object({
    ...BaseRentalAgreementSchemaShape,
    rental_agreement_type: z.literal('fixed'),
});
const IndexedRentalAgreementSchema = z.object({
    ...BaseRentalAgreementSchemaShape,
    rental_agreement_type: z.literal('indexed'),
});
const UnknownRentalAgreementSchema = z.object({
    ...BaseRentalAgreementSchemaShape,
    rental_agreement_type: z.literal('unknown'),
});
export const OverUnderRentParameterSchema = z.object({
    apply: z.boolean(),
    adjusted_discount_rate: PercentageSchema.optional(),
});
const GraduationSchema = z.object({
    rent: RentSchema,
    date: ISODateSchema,
    over_under_rent_parameter: OverUnderRentParameterSchema.optional(),
});
const GraduatedRentalAgreementSchema = z.object({
    ...BaseRentalAgreementSchemaShape,
    rental_agreement_type: z.literal('graduated'),
    graduations: GraduationSchema.array(),
});
const RentalAgreementSchema = z.discriminatedUnion('rental_agreement_type', [
    FixedRentalAgreementSchema,
    IndexedRentalAgreementSchema,
    UnknownRentalAgreementSchema,
    GraduatedRentalAgreementSchema,
]);
const VacancySpecialPropertyFeatureSchema = z.object({
    apply: z.boolean(),
    adjusted_discount_rate: PercentageSchema.optional(),
    remaining_operating_cost: ManagementCostParameterSchema,
    fitout_cost: ManagementCostParameterSchema,
    marketing_cost: ManagementCostParameterSchema,
});
const VacancyPeriodSchema = z.object({
    start_date: ISODateSchema.optional(),
    end_date: ISODateSchema.optional(),
    end_date_mode: z.enum(['date', 'duration']).optional(),
    duration: z.number().optional(),
    special_property_feature_parameter: VacancySpecialPropertyFeatureSchema.optional(),
});
const RentInstallmentSchema = z.object({
    monthly_rent: MonetaryValueSchema,
    monthly_rent_per_unit: MonetaryValueSchema,
    monthly_rent_per_sqm: MonetaryValueSchema.optional(),
    yearly_rent: MonetaryValueSchema,
    yearly_rent_per_sqm: MonetaryValueSchema.optional(),
});
export const RentalAgreementTypeSchema = z.enum(['fixed', 'indexed', 'graduated', 'unknown']);
export const RentalStatusSchema = z.enum(['rented', 'unrented', 'self_used']);
export const TenantSchema = z.object({
    uuid: UUIDSchema,
    reference_number: z.string().optional(),
    floor: z.string().optional(),
    usage_type: TenantSummaryUsageTypeSchema,
    sub_usage_type: z.string().optional(),
    area: AreaValueSchema.optional(),
    number_of_units: z.number(),
    note: z.string().optional(),
    rental_agreement: RentalAgreementSchema.optional(),
    rental_status: RentalStatusSchema,
    vacancy_periods: VacancyPeriodSchema.array(),
    rent_details: RentInstallmentSchema,
    category: z.string().optional(),
});
export const CreateTenantSchema = TenantSchema.omit({ uuid: true, rent_details: true, category: true });
export const TenantAggregateSchema = z.object({
    usage_type: TenantSummaryUsageTypeSchema.optional(),
    sub_usage_type: z.string().optional(),
    rental_status: RentalStatusSchema.optional(),
    category: z.string().optional(),
    total_area: AreaValueSchema,
    total_vacancy_area: AreaValueSchema,
    total_rented_area: AreaValueSchema,
    total_number_of_units: z.number(),
    total_vacancy_number_of_units: z.number(),
    total_rented_number_of_units: z.number(),
    total_vacancy_percentage: PercentageSchema,
    contract_rent: RentSummarySchema,
});
export const ValuationTenantSummarySchema = z.object({
    tenant_category_aggregates: TenantAggregateSchema.array(),
    tenant_usage_type_aggregates: TenantAggregateSchema.array(),
    total: TenantAggregateSchema,
});
